import styled from "@emotion/styled/macro";
import {
  Button,
  Checkbox,
  LoadingIndicator,
  LoadingOverlay,
  MOBILE_BREAKPOINT,
  Modal,
  Snackbar,
  tokens,
  useSingletonMobileMediaQuery,
  useSingletonTabletMediaQuery,
  UtilityConnectionContainer,
} from "@sunrun/experience-ui-components";
import { isSunrunManaged } from "@sunrun/sales-experience-shared";
import { useMediaQuery } from "react-responsive";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useFlags, useFlagsmith } from "flagsmith/react";
import {
  ListOffer,
  ReviewCashSummary,
  ReviewFlexMonthlySummary,
  ReviewOffer,
  ReviewPrepaidSummary,
} from "../../../amplify/backend/function/offerexpstoreFrontApi/ts/public/offerTypes";
import { Mixpanel } from "../../providers/mixpanel";
import { MobileAppDownloadBanner } from "../../cap/components/molecules/MobileAppDownloadBanner";
// eslint-disable-next-line import/no-restricted-paths
import { useCustomerSalesRep } from "../../hooks/useCustomerSalesRep";
import { useUserProfile } from "cap/hooks/useCapSwr";
import { SimplifiedOfferReviewContent } from "components/organisms/SimplifiedOfferReviewContent";
import { Disclaimer } from "components/atoms/Disclaimer";
import {
  customerGetOffer,
  customerListOffers,
  deleteOffer,
  getOffer,
  listOffers,
} from "services/storeFront";
import { useAppSelector } from "store";
import { EnvironmentalImpactOrganism } from "components/organisms/EnvironmentalImpactOrganism";
import { SimplifiedOfferReviewAlertBanners } from "components/organisms/SimplifiedOfferReviewAlertBanners";
import { SimplifiedOfferReviewHeader } from "components/organisms/SimplifiedOfferReviewHeader";
import { SimplifiedOfferReviewPaymentSection } from "components/organisms/SimplifiedOfferReviewPaymentSection";
import { OfferReviewSystemComparisonSection } from "components/organisms/OfferReviewSystemComparisonSection";
import { OfferReviewSimplifiedActionBar } from "components/organisms/OfferReviewSimplifiedActionBar";
import {
  getFlexActionBarPaymentDisplay,
  getOfferReviewSummaryMonthlyPayment,
} from "components/organisms/offerReviewPayment/offerReviewPaymentUtils";
import { isNewHomesChannel } from "utils/newHomes";
import { excludeBuilderOffers, isBuilderOffer } from "utils/builderOffer";
import {
  reloadMFE,
  useNavigateToCheckout,
  useNavigateToConfirm,
  useNavigateToOfferSelector,
  useNavigateToList,
  useNavigateToReview,
} from "utils/useNavigateHelper";

import { CardCarouselData } from "providers/Types";
import { ShareProspectForm } from "components/organisms/ShareProspectForm";
import {
  backupTimeCardData,
  consumptionCardData,
  currentUsageCardData,
  energyOffsetCardData,
  maxPowerCardData,
  productionCardData,
  storageCapacityCardData,
  systemSizeCardData,
  usageOffsetCardData,
  selfSupplyCardData,
} from "components/atoms/UsageOffsetCardData";
import { ErrorComponent } from "components/atoms/ErrorComponent";
import { getOfferFromProposalId, getOfferToShow } from "utils/offerListHelpers";
import { FeatureFlags } from "fixtures/features";
import { useContactsData, useUsageData } from "utils/swrHooks";
import { calculateUsageData } from "utils/usageUtils";
import { getOfferContact, getPrimaryContact } from "utils/contacts";
import { useAuthToken } from "hooks/useAuthToken";
import { FormReferral } from "components/organisms/Referral";
import {
  CUSTOMER_SELF_CHECKOUT__CLICKED_CHECKOUT_CTA_ON_OFFER_REVIEW_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_CONTACT_SALES_REP_CTA_OFFER_REVIEW_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_SALES_REP_CTA_OFFER_REVIEW_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_PHONE_SALES_REP_CTA_OFFER_REVIEW_PAGE,
  CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_MESSAGE_SALES_REP_CTA_OFFER_REVIEW_PAGE,
} from "constants/mixpanelEvents";
import { useProspect } from "checkout/hooks/useCheckoutSwr";
import { useCheckoutDocViewing } from "checkout/hooks/useCheckoutDocViewing";
import { ContactSalesRepModal } from "components/molecules/ContactSalesRepModal";
import {
  BACKUP_ID,
  STANDALONE_BUNDLE_ID,
  STANDALONE_PRODUCT_ID,
  STORAGE_ADDON_BUNDLE_ID,
  STORAGE_ADDON_PRODUCT_ID,
} from "constants/bundleAndProductIds";
import { FAQ } from "components/organisms/FAQ";
import { NewHomesExpandableIframe } from "components/organisms/NewHomesExpandableIframe";
import { UtilityBillSlideout } from "components/organisms/UtilityBillSlideout";
import { UsageFormContextProvider } from "providers/UsageFormContext";
import { useGetUtilityConnectionUrl } from "hooks/useGetUtilityConnectionUrl";
import { useGetUtilityConnectionStatus } from "hooks/useGetUtilityConnectionStatus";
import {
  getSelfSupplyEligibleMarkets,
  isSelfSupplyEligible,
} from "utils/selfSupplyEligibility";
import {
  hasAnnualSelfSupplyData,
  hasMonthlySelfSupplyData,
} from "utils/selfSupplyChartData";
import { formatOfferReviewProspectAddress } from "utils/formatOfferReviewProspectAddress";
import { shouldShowOfferReviewSystemComparison } from "utils/shouldShowOfferReviewSystemComparison";
import { useContractReview } from "hooks/useContractReview";
import { useAppliedPromotions } from "hooks/useAppliedPromotions";
import { filterKnownContractReviewMessages } from "utils/contractReviewUtils";
import { shouldShowAppliedPromotions } from "utils/shouldShowAppliedPromotions";
import { OfferReviewContractReviewIssuesModal } from "components/molecules/OfferReviewContractReviewIssuesModal";

const buildLegacySolarCarouselCards = (
  offer: ReviewOffer | undefined
): CardCarouselData[] =>
  [
    systemSizeCardData(offer),
    productionCardData(offer),
    usageOffsetCardData(offer),
    consumptionCardData(offer),
  ] as CardCarouselData[];

const buildSelfSupplySolarCarouselCards = (
  offer: ReviewOffer | undefined
): CardCarouselData[] => {
  if (!hasAnnualSelfSupplyData(offer)) {
    return buildLegacySolarCarouselCards(offer);
  }

  return [
    systemSizeCardData(offer),
    productionCardData(offer),
    currentUsageCardData(offer),
    energyOffsetCardData(offer),
    selfSupplyCardData(offer),
  ] as CardCarouselData[];
};

type SimplifiedOfferReviewPageProps = {
  prefetchedOffer?: ReviewOffer;
};

const SimplifiedOfferReviewPage = ({
  prefetchedOffer,
}: SimplifiedOfferReviewPageProps) => {
  const navigateToList = useNavigateToList();
  const navigateToReview = useNavigateToReview();
  const navigateToCheckout = useNavigateToCheckout();
  const navigateToConfirm = useNavigateToConfirm();
  const navigateToOfferSelector = useNavigateToOfferSelector();
  const isMobile = useSingletonMobileMediaQuery();
  const isTablet = useSingletonTabletMediaQuery();
  const isTabletPortrait = useMediaQuery({
    maxWidth: 850,
    orientation: "portrait",
  });
  const {
    prospectId,
    offerId: offerIdParam,
    proposalId: proposalIdParam,
  } = useParams();

  const cognitoToken = useAppSelector(
    (state) => state.auth.cognitoToken?.token
  );
  const proposalToken = useAuthToken("PROPOSAL")?.token;
  const isCustomer = !!proposalToken;
  const hybridToken = useAppSelector((state) => state.auth.hybridToken);
  const { userProfile, userProfileLoading } = useUserProfile(hybridToken);

  const {
    prospect: usage,
    error: usageError,
    attachments,
  } = useUsageData(prospectId);

  const customerState = usage?.customerState ?? "";
  const utilityCompany = usage?.utilityCompany ?? "";

  const identity = useAppSelector((state) => state.auth.identity);
  const [defaultOfferId, setDefaultOfferId] = useState<string>();
  const [offer, setOffer] = useState<ReviewOffer | undefined>(prefetchedOffer);
  const offerRef = useRef<ReviewOffer>();
  offerRef.current = offer;
  const [isLoading, setIsLoading] = useState<boolean>(!prefetchedOffer);
  const [offers, setOffers] = useState<ListOffer[]>();
  const [isOffersLoading, setIsOffersLoading] = useState<boolean>(true);
  const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
  const [showShareModal, setShowShareModal] = useState<boolean>(false);
  const [loadingOverlay, setLoadingOverlay] = useState<string | undefined>(
    undefined
  );
  const [snackBarState, setSnackBarState] = useState<
    | {
        type: "success" | "error" | "warning" | "info";
        message: string;
        onHide?: () => void;
      }
    | undefined
  >(undefined);
  const [selectedOffers, setSelectedOffers] = useState<string[]>([]);
  const [error, setError] = useState<Error>();
  const [showDisclaimerModal, setShowDisclaimerModal] = useState(false);
  const [showSolarStepsDetails, setShowSolarStepsDetails] = useState(true);
  const [salesRepContactModalOpen, setSalesRepContactModalOpen] =
    useState(false);
  const [showMapModalOpen, setShowMapModalOpen] = useState(false);
  const [showUtilityBillSlideout, setShowUtilityBillSlideout] = useState(false);
  const [showGetBillOnlineIframe, setShowGetBillOnlineIframe] = useState(false);
  const [contractReviewModalOpen, setContractReviewModalOpen] = useState(false);

  const flagsmith = useFlagsmith();
  const flags = useFlags([
    FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED,
    FeatureFlags.SELF_SUPPLY,
    FeatureFlags.CONTRACT_REVIEW,
    FeatureFlags.NEW_OFFER_EXP_PROMOTIONS,
  ]);

  const qualifiesForUtilityBillPastDueBalanceFlow =
    offer?.financial?.summary?.type === "FlexMonthly" ||
    offer?.financial?.summary?.type === "Monthly";
  const utilityBillPastDueBalanceFlowIsEnabled =
    (flags[FeatureFlags.UTILITY_PAST_DUE_BALANCE_FLOW_ENABLED]?.enabled &&
      qualifiesForUtilityBillPastDueBalanceFlow) ??
    false;

  const selfSupplyFlag = flags[FeatureFlags.SELF_SUPPLY];
  const selfSupplyEligibleMarkets = getSelfSupplyEligibleMarkets(
    flagsmith.getValue(FeatureFlags.SELF_SUPPLY, { json: true, fallback: [] })
  );
  const showPromotions = shouldShowAppliedPromotions({
    flagEnabled: flags[FeatureFlags.NEW_OFFER_EXP_PROMOTIONS]?.enabled ?? false,
    isCustomer,
    isPartner: userProfile?.isPartner,
    userProfileLoading,
  });

  const { promotions } = useAppliedPromotions(
    offer?.proposalId,
    prospectId,
    showPromotions
  );

  const showSelfSupply =
    selfSupplyFlag?.enabled &&
    isSelfSupplyEligible(
      customerState,
      utilityCompany,
      selfSupplyEligibleMarkets
    );

  const isNewHomes = isNewHomesChannel(offer?.channel);

  const hasBattery = [
    ...(offer?.includedProducts || []),
    ...(offer?.accessoryProducts || []),
  ]
    // Checks all product types just to be safe in case we add new product types in the future.
    .some((product) =>
      [
        STORAGE_ADDON_BUNDLE_ID,
        STORAGE_ADDON_PRODUCT_ID,
        BACKUP_ID,
        STANDALONE_BUNDLE_ID,
        STANDALONE_PRODUCT_ID,
      ].includes(product.productId)
    );
  const nonSolarView = Boolean(offer?.isNonSolar);

  const showSystemComparisonSection = shouldShowOfferReviewSystemComparison({
    offer,
    nonSolarView,
    hasBattery,
  });

  const showActionBarPaymentDisclaimer =
    Boolean(offer?.financial?.summary) && !isNewHomes;

  const graphSectionRef = useRef<HTMLDivElement>(null);
  const [energyProfileGraphSelectNonce, setEnergyProfileGraphSelectNonce] =
    useState(0);

  const showEnergyProfileGraph = hasMonthlySelfSupplyData(offer);
  const showSelfSupplyGraphLink = showEnergyProfileGraph;

  const handleSelfSupplyGraphClick = useCallback(() => {
    setEnergyProfileGraphSelectNonce((nonce) => nonce + 1);
    graphSectionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
  }, []);

  const offerId = useMemo(() => {
    return offerIdParam ?? defaultOfferId;
  }, [offerIdParam, defaultOfferId]);

  const visibleOffers = useMemo(() => {
    if (!offers) {
      return undefined;
    }

    return isCustomer ? excludeBuilderOffers(offers) : offers;
  }, [offers, isCustomer]);

  const offerOptions = useMemo(() => {
    if (!visibleOffers) {
      return [];
    }

    return visibleOffers.map((e) => {
      return { label: e.label, value: e.id };
    });
  }, [visibleOffers]);

  const isBuilderOfferSelected = Boolean(offer && isBuilderOffer(offer));
  const isCheckoutDisabled =
    isBuilderOfferSelected ||
    (offer?.isExpired ? !offer.isSigned : Boolean(offer?.isExpired));

  const { contacts } = useContactsData(prospectId!, hybridToken);
  const showReferralForm =
    !isCustomer && Boolean(userProfile && isSunrunManaged(userProfile));
  const referralContactId = getPrimaryContact(contacts)?.contactId;

  const offerContact = useMemo(
    () => getOfferContact(contacts, offers),
    [contacts, offers]
  );

  const hasUploadedUtilityBill = Boolean(
    attachments?.some(
      (attachment) => attachment.documentType === "Utility Bill"
    )
  );

  const { monthsEntered } = calculateUsageData({ prospect: usage });

  const showUtilityBillUploadBanner =
    !hasUploadedUtilityBill && monthsEntered > 0 && monthsEntered <= 12;

  const showSquareFootageEstimateBanner =
    !isNewHomes &&
    !nonSolarView &&
    !hasUploadedUtilityBill &&
    monthsEntered === 0 &&
    (offer?.usage.homeSizeEstimate ?? 0) > 0;

  const isContractReviewEnabled =
    flags[FeatureFlags.CONTRACT_REVIEW]?.enabled ?? false;

  const shouldShowOfferReviewContractReview =
    isContractReviewEnabled &&
    !isCustomer &&
    Boolean(offer?.proposalId) &&
    hasUploadedUtilityBill;

  const { messages: contractReviewMessages } = useContractReview(
    shouldShowOfferReviewContractReview ? offer?.proposalId : undefined
  );

  const contractReviewIssues = useMemo(
    () => filterKnownContractReviewMessages(contractReviewMessages),
    [contractReviewMessages]
  );

  const isFlexMonthlyOffer = offer?.financial?.summary?.type === "FlexMonthly";
  const isPrepaidOffer =
    offer?.financial?.summary?.type === "Prepaid" ||
    offer?.financial?.summary?.type === "Cash";

  const actionBarMonthlyPayment = useMemo(
    () =>
      offer && !isFlexMonthlyOffer && !isPrepaidOffer
        ? getOfferReviewSummaryMonthlyPayment(offer.financial.summary)
        : undefined,
    [offer, isFlexMonthlyOffer, isPrepaidOffer]
  );

  const actionBarFlexPayment = useMemo(() => {
    if (!offer || !isFlexMonthlyOffer) {
      return null;
    }
    return getFlexActionBarPaymentDisplay(
      offer.financial.summary as ReviewFlexMonthlySummary
    );
  }, [offer, isFlexMonthlyOffer]);

  const actionBarDueAtSigning = offer?.financial?.summary?.dueAtSigning ?? 0;
  const actionBarPrepaidTotal =
    offer && isPrepaidOffer
      ? (offer.financial.summary as ReviewPrepaidSummary | ReviewCashSummary)
          .total
      : undefined;

  const {
    getUrl,
    loading: billOnlineLoading,
    url: billOnlineUrl,
  } = useGetUtilityConnectionUrl(hybridToken);
  const {
    status: intervalDataStatus,
    initialLoading: intervalDataInitialLoading,
    startPolling,
  } = useGetUtilityConnectionStatus(hybridToken, prospectId || null);

  const onGetBillOnline = useCallback(() => {
    if (prospectId && !intervalDataStatus) {
      getUrl(prospectId);
      setShowGetBillOnlineIframe(true);
    }
  }, [prospectId, intervalDataStatus, getUrl]);

  const { data: prospect } = useProspect(prospectId);
  const headerAddress = useMemo(
    () => formatOfferReviewProspectAddress(usage ?? prospect),
    [usage, prospect]
  );
  const { data: salesRep, error: salesRepError } = useCustomerSalesRep();

  const { viewDocument, viewDocLoading } = useCheckoutDocViewing(
    prospectId!,
    offer?.proposalId!,
    "Customer Agreement"
  );
  useEffect(() => {
    if (((!cognitoToken || !identity) && !proposalToken) || !offerId) return;
    if (offerRef.current?.id === offerId) return;
    const isRefreshingOffer = Boolean(offerRef.current);
    if (!isRefreshingOffer) {
      setIsLoading(true);
    }
    let request;
    if (cognitoToken && identity) {
      request = getOffer(cognitoToken, offerId, identity);
    } else if (proposalToken) {
      request = customerGetOffer(proposalToken, offerId);
    } else {
      setIsLoading(false);
      setError(new Error(`Invalid token type.`));
      return;
    }
    request
      .then((data) => {
        if (data) {
          setOffer(data);
        }
      })
      .catch((error) => {
        setError(
          new Error("There was a problem fetching the offer data.", {
            cause: error,
          })
        );
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [cognitoToken, proposalToken, offerId, identity]);

  useEffect(() => {
    if (((!cognitoToken || !identity) && !proposalToken) || !prospectId) return;
    setIsOffersLoading(true);
    let request;
    if (cognitoToken && identity) {
      request = listOffers(cognitoToken, prospectId, identity);
    } else if (proposalToken) {
      request = customerListOffers(proposalToken);
    } else {
      setIsOffersLoading(false);
      setError(new Error("Invalid token type."));
      return;
    }
    request
      ?.then((data) => {
        if (data && data.length > 0) {
          const visibleOffers = isCustomer ? excludeBuilderOffers(data) : data;
          setOffers(data);

          if (!offerId) {
            if (!visibleOffers.length) {
              navigateToList();
              return;
            }

            let offerToShow;
            if (proposalIdParam) {
              offerToShow =
                getOfferFromProposalId(visibleOffers, proposalIdParam) ??
                getOfferToShow(visibleOffers);
            } else {
              offerToShow = getOfferToShow(visibleOffers);
            }

            setDefaultOfferId(offerToShow.id);
            navigateToReview(offerToShow.id);
          }
        } else {
          navigateToList();
        }
      })
      .catch((error) => {
        setIsOffersLoading(false);
        setError(
          new Error("There was a problem fetching the offer list data.", {
            cause: error,
          })
        );
      })
      .finally(() => {
        setIsOffersLoading(false);
      });
  }, [
    cognitoToken,
    navigateToList,
    navigateToReview,
    offerId,
    proposalIdParam,
    proposalToken,
    prospectId,
    identity,
    isCustomer,
  ]);

  const oneOffer = offers?.length === 1;

  const closeRemoveModal = useCallback(() => {
    setShowRemoveModal(false);
  }, []);
  const closeShareModal = useCallback(() => {
    setShowShareModal(false);
  }, []);
  const onExitClick = useCallback(() => {
    navigateToList();
  }, [navigateToList]);

  const onContactSalesRepClick = useCallback(() => {
    Mixpanel.track(
      CUSTOMER_SELF_CHECKOUT__CLICKED_CONTACT_SALES_REP_CTA_OFFER_REVIEW_PAGE,
      {
        prospectId,
        offerId: offer?.id,
      }
    );
    setSalesRepContactModalOpen(true);
  }, [prospectId, offer]);

  const onCheckoutClick = useCallback(() => {
    if (isCustomer) {
      Mixpanel.track(
        CUSTOMER_SELF_CHECKOUT__CLICKED_CHECKOUT_CTA_ON_OFFER_REVIEW_PAGE,
        {
          prospectId,
          offerId: offer?.id,
        }
      );
    }
    navigateToCheckout("confirm-offer", offer!.id);
  }, [isCustomer, prospectId, navigateToCheckout, offer]);

  const handleGoToConfirmInfo = useCallback(() => {
    setContractReviewModalOpen(false);
    navigateToConfirm();
  }, [navigateToConfirm]);

  const onSelectAllOffer = useCallback(
    (selected: boolean) => {
      if (selected) {
        setSelectedOffers(offerOptions!.map((e) => e.value));
      } else {
        setSelectedOffers([]);
      }
    },
    [setSelectedOffers, offerOptions]
  );
  const onSelectOffer = useCallback(
    (offerId: string, selected: boolean) => {
      if (selected) {
        setSelectedOffers([...selectedOffers, offerId]);
      } else {
        setSelectedOffers(selectedOffers.filter((item) => item !== offerId));
      }
    },
    [setSelectedOffers, selectedOffers]
  );
  const onRemoveSelectedOffers = useCallback(() => {
    if (!cognitoToken || !offers) {
      return;
    }
    closeRemoveModal();
    setLoadingOverlay(
      `Removing ${selectedOffers.length > 1 ? "offers" : "offer"} ...`
    );
    const selectedOfferObjects = offers.filter((e) =>
      selectedOffers.includes(e.id)
    );
    const promises = [] as Promise<void>[];
    selectedOfferObjects.forEach((selectedOffer) => {
      promises.push(
        deleteOffer(cognitoToken, selectedOffer.id, selectedOffer.lockVersion)
      );
    });
    Promise.all(promises).finally(() => {
      // Succuss or failure, reload the page to show the current state
      reloadMFE();
    });
  }, [selectedOffers, cognitoToken, offers, closeRemoveModal]);
  const onSendOffersStart = useCallback(() => {
    closeShareModal();
    setLoadingOverlay(
      `Sending ${offers?.length && offers.length > 1 ? "offers" : "offer"}...`
    );
  }, [closeShareModal, offers?.length]);
  const onSendOffers = useCallback(
    (type: "success" | "error" | "warning" | "info", message: string) => {
      setLoadingOverlay(undefined);
      setSnackBarState({
        type,
        message,
        onHide: () => {
          reloadMFE();
        },
      });
    },
    []
  );
  const systemCarouselData = useMemo(() => {
    const cards = showSelfSupply
      ? buildSelfSupplySolarCarouselCards(offer)
      : buildLegacySolarCarouselCards(offer);

    const nonSolarOnCards = [
      storageCapacityCardData(offer),
      maxPowerCardData(offer),
      backupTimeCardData(offer),
    ] as CardCarouselData[];

    const newHomesCards = [
      systemSizeCardData(offer),
      productionCardData(offer),
    ] as CardCarouselData[];

    if (nonSolarView) {
      return nonSolarOnCards;
    }

    if (isNewHomes) {
      return newHomesCards;
    }

    return cards;
  }, [offer, nonSolarView, isNewHomes, showSelfSupply]);

  if (!prospectId) {
    console.error(
      "Prospect Id is required from the URL! prospectId:",
      prospectId
    );
    return (
      <ErrorComponent
        context="ReviewPage"
        action="Get Prospect"
        error={new Error(`Prospect Id is missing: ${prospectId}`)}
      />
    );
  }

  if (error) {
    return (
      <ErrorComponent context="ReviewPage" action="Get Offer" error={error} />
    );
  }

  if (usageError) {
    return (
      <ErrorComponent
        context="ReviewPage"
        action="Get Usage Data"
        error={usageError}
      />
    );
  }

  if (!offer && isLoading) {
    return (
      <LoadingIndicator color="black" fullScreen={true}></LoadingIndicator>
    );
  }

  if (!offer) {
    return (
      <ErrorComponent
        context="ReviewPage"
        action="Get Offer"
        error={new Error("Offer data is unavailable.")}
      />
    );
  }

  return (
    <>
      <StyledBgContainer>
        <SimplifiedOfferReviewHeader
          offer={offer}
          address={headerAddress ?? ""}
          utilityCompany={utilityCompany}
          customerState={customerState}
          isMobile={isMobile}
          isOffersLoading={isOffersLoading}
          offers={visibleOffers}
          cognitoToken={cognitoToken}
          isCustomer={isCustomer}
          onExitClick={onExitClick}
          navigateToOfferSelector={navigateToOfferSelector}
          showMapModalOpen={showMapModalOpen}
          setShowMapModalOpen={setShowMapModalOpen}
          showIntroTagline={showSystemComparisonSection}
        />
        {showSystemComparisonSection ? (
          <OfferReviewSystemComparisonSection
            offer={offer}
            utilityCompany={utilityCompany}
          />
        ) : null}
        <SimplifiedOfferReviewPaymentSection
          offer={offer}
          hasBattery={hasBattery}
          isNewHomes={isNewHomes}
          promotions={promotions}
          showPromotions={showPromotions}
          omitInlineMonthlyAutopayDisclaimer={showActionBarPaymentDisclaimer}
          hideOfferExpiryFootnote={showActionBarPaymentDisclaimer}
          showSelfSupplyGraphLink={showSelfSupplyGraphLink}
          onSelfSupplyGraphClick={handleSelfSupplyGraphClick}
        />
        <SimplifiedActionBarStack>
          <OfferReviewSimplifiedActionBar
            embedded
            monthlyPayment={actionBarMonthlyPayment}
            flexPayment={actionBarFlexPayment}
            isPrepaid={isPrepaidOffer}
            totalAmount={actionBarPrepaidTotal}
            dueAtSigning={actionBarDueAtSigning}
            onCheckoutClick={onCheckoutClick}
            checkoutDisabled={isCheckoutDisabled}
            isCustomer={isCustomer}
            isNewHomes={isNewHomes}
            onUtilityBillClick={() => setShowUtilityBillSlideout(true)}
            onShareClick={() => setShowShareModal(true)}
            hasUploadedUtilityBill={hasUploadedUtilityBill}
            offerExpires={offer.financial.summary.expires}
            isMobile={isMobile}
          />
        </SimplifiedActionBarStack>
        <SimplifiedOfferReviewAlertBanners
          offer={offer}
          preferredLanguage={prospect?.preferredLanguage}
          viewDocLoading={viewDocLoading}
          onViewDocument={viewDocument}
          utilityBillPastDueBalanceFlowIsEnabled={
            utilityBillPastDueBalanceFlowIsEnabled
          }
          utilityCompany={usage?.utilityCompany}
          utilityPastDueBalance={usage?.utilityPastDueBalance}
          showUtilityBillUploadBanner={showUtilityBillUploadBanner}
          showSquareFootageEstimateBanner={showSquareFootageEstimateBanner}
          onUtilityBillClick={() => setShowUtilityBillSlideout(true)}
        />
        <SimplifiedOfferReviewContent
          offer={offer}
          prospect={prospect}
          prospectId={prospectId!}
          nonSolarView={nonSolarView ?? false}
          hasBattery={hasBattery}
          isMobile={isMobile}
          isTablet={isTablet}
          isCustomer={isCustomer}
          systemCarouselData={systemCarouselData}
          showSolarStepsDetails={showSolarStepsDetails}
          setShowSolarStepsDetails={setShowSolarStepsDetails}
          onContactSalesRepClick={onContactSalesRepClick}
          onCheckoutClick={onCheckoutClick}
          setShowMapModalOpen={setShowMapModalOpen}
          isNewHomes={isNewHomes}
          showSelfSupply={showEnergyProfileGraph}
          promotions={promotions}
          showPromotions={showPromotions}
          energyProfileGraphSelectNonce={energyProfileGraphSelectNonce}
          graphSectionRef={graphSectionRef}
        />

        {isNewHomes && <NewHomesExpandableIframe />}

        <MobileAppDownloadBanner
          pageContext="Offer Review"
          layout="new"
          isNewHomes={isNewHomes}
          isCustomer={isCustomer}
        />
        {!nonSolarView && <FAQ offer={offer} isNewHomes={isNewHomes} />}
        {showReferralForm && (
          <ReferralFormContainer>
            <FormReferral contactId={referralContactId} />
          </ReferralFormContainer>
        )}
        <EnvironmentalImpactOrganism
          data-testid="Review-Page-Environmental-Impact-Section"
          kWhPerYear={offer.production || 0}
        />
        {salesRepContactModalOpen && !salesRepError && salesRep && (
          <ContactSalesRepModal
            testId="review-page-sales-rep-contact-modal"
            open={salesRepContactModalOpen}
            onClose={() => setSalesRepContactModalOpen(false)}
            prospectId={prospectId}
            offerId={offer?.id}
            isCustomer={isCustomer}
            salesRep={salesRep}
            mixpanelEmailEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_EMAIL_SALES_REP_CTA_OFFER_REVIEW_PAGE
            }
            mixpanelPhoneEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_PHONE_SALES_REP_CTA_OFFER_REVIEW_PAGE
            }
            mixpanelMessageEvent={
              CUSTOMER_SELF_CHECKOUT__CLICKED_SEND_MESSAGE_SALES_REP_CTA_OFFER_REVIEW_PAGE
            }
          />
        )}
        <StyledLauncherContainer>
          <DisclaimerLinkButton
            color="link"
            size="sm"
            onClick={() => setShowDisclaimerModal(true)}
          >
            View Disclaimers
          </DisclaimerLinkButton>
        </StyledLauncherContainer>
        {shouldShowOfferReviewContractReview && (
          <OfferReviewContractReviewIssuesModal
            open={contractReviewModalOpen}
            messages={contractReviewIssues}
            onClose={() => setContractReviewModalOpen(false)}
            onGoToConfirmInfo={handleGoToConfirmInfo}
          />
        )}
      </StyledBgContainer>
      {showRemoveModal && (
        <Modal onClose={closeRemoveModal} testId="review-page-remove-modal">
          <ModalTitle>Select Offers to Remove</ModalTitle>
          <CheckboxContainer>
            <div>
              <Checkbox
                label="All current proposals (toggle to deselect all)"
                checked={selectedOffers.length === offers!.length}
                onChange={(event) => onSelectAllOffer(event.target.checked)}
              />
            </div>
            {offerOptions!.map((option) => {
              return (
                <div key={option.value}>
                  <Checkbox
                    label={option.label}
                    checked={selectedOffers.includes(option.value)}
                    onChange={(event) =>
                      onSelectOffer(option.value, event.target.checked)
                    }
                  />
                </div>
              );
            })}
          </CheckboxContainer>
          <ModalButtonContainer>
            <Button
              style={{ width: 155, marginLeft: "auto" }}
              disabled={selectedOffers.length === 0}
              onClick={onRemoveSelectedOffers}
            >
              Remove
            </Button>
          </ModalButtonContainer>
        </Modal>
      )}
      {showShareModal && offers && (
        <StyledShareModal
          testId="review-page-share-modal"
          onClose={closeShareModal}
          customModalWidth={isTabletPortrait ? 591 : 900}
          title={oneOffer ? "Share Offer" : "Share Offers"}
          hideIcon
        >
          <ShareProspectForm
            isPartner={userProfile?.isPartner}
            prospectId={prospectId}
            offers={offers}
            customerName={
              offerContact?.customerFirstName +
              " " +
              offerContact?.customerLastName
            }
            customerEmail={offerContact?.customerEmail}
            customerPrimaryPhone={offerContact?.customerPrimaryPhone}
            onSubmitStart={onSendOffersStart}
            onSubmitComplete={onSendOffers}
            closeModal={closeShareModal}
          />
        </StyledShareModal>
      )}
      {!!loadingOverlay?.length && (
        <LoadingOverlay
          message={loadingOverlay}
          data-testid="review-page-loading-modal"
        />
      )}
      {snackBarState && (
        <Snackbar
          open={!!snackBarState}
          onClose={() => {
            snackBarState.onHide?.();
            setSnackBarState(undefined);
          }}
          type={snackBarState.type}
          message={snackBarState.message}
          data-testid="review-page-snackbar"
        />
      )}
      {showDisclaimerModal && (
        <StyledModal
          testId="review-page-disclaimer-modal"
          title="Disclaimers"
          onClose={() => setShowDisclaimerModal(false)}
          hideIcon
        >
          <Disclaimer />
        </StyledModal>
      )}
      {showUtilityBillSlideout && (
        <UsageFormContextProvider>
          <UtilityBillSlideout
            onClose={() => setShowUtilityBillSlideout(false)}
            onGetBillOnline={onGetBillOnline}
            intervalDataInitialLoading={intervalDataInitialLoading}
            intervalDataStatus={intervalDataStatus}
          />
        </UsageFormContextProvider>
      )}
      {showGetBillOnlineIframe && (
        <UtilityConnectionContainer
          src={billOnlineUrl || ""}
          isLoading={billOnlineLoading}
          onClose={() => {
            setShowGetBillOnlineIframe(false);
            startPolling();
          }}
          onError={() => setShowGetBillOnlineIframe(false)}
          returnButtonText="Return to offer review"
        />
      )}
    </>
  );
};

const StyledModal = styled(Modal)`
  > .modal-container {
    height: 90%;
    width: 90%;
    overflow: scroll;

    > #modal-header {
      padding-bottom: 0px;
    }
  }
`;

const StyledBgContainer = styled.div`
  background-color: ${tokens.BRAND_OFF_WHITE};
  background-image: linear-gradient(
    180deg,
    ${tokens.BRAND_CHARGE_BLUE} 0px,
    ${tokens.BRAND_OFF_WHITE} 900px
  );
  min-height: 100vh;
`;

const SimplifiedActionBarStack = styled.div`
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto 48px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 32px);
  }
`;

const ReferralFormContainer = styled.div`
  box-sizing: border-box;
  max-width: 1005px;
  width: calc(100% - 128px);
  margin: 0 auto 64px;

  @media (max-width: ${MOBILE_BREAKPOINT}px) {
    width: calc(100% - 32px);
  }
`;

const ModalTitle = styled.div`
  text-align: left;
  font-size: 44px;
  margin-bottom: 0.5rem;
`;

const CheckboxContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const ModalButtonContainer = styled.div`
  margin-top: 1rem;
`;

const StyledLauncherContainer = styled.div`
  width: 94%;
  margin: 20px 0 20px auto;
`;

const DisclaimerLinkButton = styled(Button)`
  &.sr-button-link:hover,
  &.sr-button-link:focus-visible {
    color: ${tokens.INFO_40};
    opacity: 1;
  }
`;

const StyledShareModal = styled(Modal)`
  .modal-content-container {
    padding: 0px 40px 40px 40px;
  }
`;

export { SimplifiedOfferReviewPage };
